import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Icon,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import { useMaterialUIController } from "context";

const modalStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "700px",
  maxHeight: "90vh",
  overflow: "auto",
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  p: 0,
};

function RegistrarProveedor({ open, onClose, onSuccess }) {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const initialFormState = {
    empresa_id: localStorage.getItem("empresaId") || "1",
    nombre: "",
    rfc: "",
    email: "",
    telefono: "",
    direccion: "",
    contacto: "",
    puesto: "",
    categorias: "",
    estatus: "activo",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [empresas, setEmpresas] = useState([]);

  useEffect(() => {
    const fetchEmpresas = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.EMPRESAS);
        setEmpresas(response.data);
      } catch (error) {
        console.error("Error al cargar empresas:", error);
      }
    };

    if (open) {
      fetchEmpresas();
      setFormData({
        ...initialFormState,
        empresa_id: localStorage.getItem("empresaId") || "1",
      });
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(API_ENDPOINTS.PROVEEDORES, formData);
      alert("Proveedor registrado exitosamente");
      if (onSuccess) onSuccess(response.data.proveedor);
      onClose();
    } catch (error) {
      console.error("Error al registrar proveedor:", error);
      if (error.response?.data?.errors) {
        const message = Object.values(error.response.data.errors).flat().join("\n");
        alert(`Errores de validación:\n${message}`);
      } else {
        alert("Error al registrar proveedor. Por favor intente de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyles}>
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <MDBox
            variant="gradient"
            bgColor={sidenavColor}
            borderRadius="8px 8px 0 0"
            p={3}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h5" color="white" fontWeight="medium">
              Registrar Nuevo Proveedor
            </MDTypography>
            <IconButton onClick={onClose} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </MDBox>

          <MDBox p={3}>
            <Grid container spacing={2}>
              {/* Información de la Empresa */}
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Empresa</InputLabel>
                  <Select
                    name="empresa_id"
                    value={formData.empresa_id}
                    onChange={handleChange}
                    label="Empresa"
                    sx={{ height: "45px" }}
                  >
                    {empresas.map((emp) => (
                      <MenuItem key={emp.id} value={emp.id}>
                        {emp.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Información Básica */}
              <Grid item xs={12}>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Información Básica</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Nombre o Razón Social"
                          name="nombre"
                          value={formData.nombre}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="RFC"
                          name="rfc"
                          value={formData.rfc}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Teléfono"
                          name="telefono"
                          value={formData.telefono}
                          onChange={handleChange}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Contacto y Dirección */}
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Contacto y Dirección</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Persona de Contacto"
                          name="contacto"
                          value={formData.contacto}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Puesto"
                          name="puesto"
                          value={formData.puesto}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Dirección"
                          name="direccion"
                          multiline
                          rows={2}
                          value={formData.direccion}
                          onChange={handleChange}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Otros Detalles */}
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Otros Detalles</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={8}>
                        <TextField
                          fullWidth
                          label="Categorías (ej: Papelería, Hardware)"
                          name="categorias"
                          value={formData.categorias}
                          onChange={handleChange}
                          placeholder="Separadas por coma"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Estatus</InputLabel>
                          <Select
                            name="estatus"
                            value={formData.estatus}
                            onChange={handleChange}
                            label="Estatus"
                            sx={{ height: "45px" }}
                          >
                            <MenuItem value="activo">Activo</MenuItem>
                            <MenuItem value="inactivo">Inactivo</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          </MDBox>

          {/* Footer Actions */}
          <MDBox p={3} display="flex" justifyContent="flex-end" gap={2}>
            <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
              Cancelar
            </MDButton>
            <MDButton type="submit" variant="gradient" color={sidenavColor} disabled={loading}>
              {loading ? "Guardando..." : "Registrar Proveedor"}
            </MDButton>
          </MDBox>
        </form>
      </Box>
    </Modal>
  );
}

RegistrarProveedor.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default RegistrarProveedor;
